"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { track } from "@vercel/analytics";
import { trackEvent } from "@/lib/analytics";
import { useMySignups } from "@/lib/hooks/useMySignups";
import { useMembership } from "@/lib/hooks/useMembership";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { CheckCircleFilled } from "@/components/icons/CheckCircleFilled";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { setReturnIntent } from "@/lib/auth/return-intent";

import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { MemberLockIcon } from "@/components/icons/MemberLockIcon";
import { EventTag, type EventTagVariant } from "@/components/layout/EventTag";
import { useAuthModal } from "@/components/auth/AuthModalContext";

type DateEntry = {
  original: string;
  display: string;
};

type EventSignupData = {
  storyblokEventUuid: string;
  eventDate: string;
  eventDisplayDate: string;
  eventTitle: string;
  eventSlug: string;
  eventType: string;
  eventLink?: string;
  speakerName?: string;
  memberOnly: boolean;
  /**
   * Wohin der Nutzer nach dem Login zurueckkehrt. Bei Serienterminen mit
   * `?date=`, damit er beim gewaehlten Termin landet und nicht beim ersten.
   *
   * Getrennt von `eventSlug`: Der geht in Analytics und Datenbank, wo das
   * Datum ohnehin als eigenes Feld steht und im Slug nur stoeren wuerde.
   */
  returnUrl?: string;
  allDates?: DateEntry[];
};

/**
 * Gemeinsames Property-Set fuer alle Event-Analytics. Vorher feuerten die vier
 * event_signup_cancelled-Stellen drei verschiedene Sets — mal mit uuid, mal
 * ohne slug —, wodurch eine Auswertung pro Event nur fuer einen Teil der
 * Abmeldungen moeglich war.
 */
function eventProps(event: EventSignupData, date?: string) {
  return {
    event_title: event.eventTitle,
    event_type: event.eventType,
    event_date: date ?? event.eventDate,
    event_slug: event.eventSlug,
    storyblok_event_uuid: event.storyblokEventUuid,
    member_only: event.memberOnly,
  };
}

type EventSignupModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventSignupData;
};

// --- Recurring event: inline date row with signup/cancel ---
const DateRow = ({
  entry,
  isSignedUp,
  event,
  onUpdate,
  onMembershipRequired,
}: {
  entry: DateEntry;
  isSignedUp: boolean;
  event: EventSignupData;
  onUpdate: () => void;
  onMembershipRequired: () => void;
}) => {
  const { refresh: refreshSignups } = useMySignups();
  const [loading, setLoading] = useState(false);
  const d = new Date(entry.display);
  const label = format(d, "EEEE, dd. MMMM yyyy", { locale: de });

  const handleSignup = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/events/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storyblokEventUuid: event.storyblokEventUuid,
          eventDate: entry.original,
          eventTitle: event.eventTitle,
          eventSlug: event.eventSlug,
          eventType: event.eventType,
          eventLink: event.eventLink,
          speakerName: event.speakerName,
          memberOnly: event.memberOnly,
          displayDate: entry.display,
        }),
      });
      if (res.status === 403) {
        // Kein Event hier — der Gate-Effekt oben deckt beide Sperren bereits ab
        // und wuerde sonst doppelt zaehlen.
        onMembershipRequired();
        return;
      }
      if (res.ok) {
        trackEvent("event_signup", eventProps(event, entry.original));
        track("Event signup", { event_title: event.eventTitle, event_type: event.eventType });
        refreshSignups();
        onUpdate();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/events/signup", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storyblokEventUuid: event.storyblokEventUuid,
          eventDate: entry.original,
        }),
      });
      if (res.ok) {
        trackEvent("event_signup_cancelled", eventProps(event, entry.original));
        track("Event signup cancelled", { event_title: event.eventTitle, event_type: event.eventType });
        refreshSignups();
        onUpdate();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-25 px-4 py-3">
      <span className="text-copy text-black">{label}</span>
      {loading ? (
        <span className="text-copy-sm text-gray-50 shrink-0">...</span>
      ) : isSignedUp ? (
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-copy-sm bg-sky-50 text-black rounded-full px-2 py-0.5 flex items-center gap-1">
            <CheckCircleFilled className="size-3" />
            Angemeldet
          </span>
          <button
            type="button"
            onClick={handleCancel}
            className="text-copy-sm text-gray-75 hover:underline hover:text-black transition-colors"
          >
            Abmelden
          </button>
        </div>
      ) : (
        <Button variant="black" size="sm" onClick={handleSignup} className="shrink-0">
          Anmelden
        </Button>
      )}
    </div>
  );
};

// --- Single event states ---
type SingleState = "confirm" | "success" | "already_signed_up" | "loading";

const EventSignupModal = ({
  open,
  onOpenChange,
  event,
}: EventSignupModalProps) => {
  const { userId } = useAuth();
  const { isMember, isLoading: membershipLoading, refresh: refreshMembership } = useMembership();
  const { refresh: refreshSignups } = useMySignups();
  const { setOpen: setAuthModalOpen } = useAuthModal();
  const isRecurring = (event.allDates?.length ?? 0) > 1;
  const [singleState, setSingleState] = useState<SingleState>("confirm");
  const [signedUpDates, setSignedUpDates] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");

  const isLoggedIn = !!userId;
  const needsLogin = !isLoggedIn;
  const memberCheckPending = event.memberOnly && membershipLoading;
  const needsMembership = event.memberOnly && !membershipLoading && !isMember && isLoggedIn;
  const canSignup = isLoggedIn && !memberCheckPending && (!event.memberOnly || isMember);

  const formattedDate = format(new Date(event.eventDisplayDate), "EEEE, dd. MMMM yyyy HH:mm 'Uhr'", {
    locale: de,
  });

  // Blockierte Anmeldeversuche sichtbar machen: wer das Modal oeffnet und ein
  // Gate sieht, wollte teilnehmen. Einmal pro Oeffnen, nicht pro Render.
  const trackedGate = useRef(false);
  useEffect(() => {
    if (!open) { trackedGate.current = false; return; }
    if (trackedGate.current || memberCheckPending) return;
    if (needsLogin) {
      trackedGate.current = true;
      // Bei einem Member-Only-Event ist der Login nicht die eigentliche
      // Huerde: Ein Konto allein bringt hier niemanden weiter, es braucht
      // eine Mitgliedschaft — genau deshalb fuehrt der Knopf unten zur
      // Planauswahl statt zum Login.
      //
      // Die Reihenfolge der Pruefung hat das bisher verdeckt. Weil `needsLogin`
      // zuerst greift, zaehlte jeder ausgeloggte Besucher eines
      // Mitglieder-Events als Login-Problem. In Production stehen deshalb 471
      // `login_required` gegen 25 `membership_required` — die 471 vermischen
      // zwei verschiedene Huerden.
      //
      // Ob die Person eine Mitgliedschaft *haette*, ist ausgeloggt nicht
      // feststellbar. Deshalb wird nicht geraten, sondern die Eigenschaft des
      // Events gemeldet: `member_only_logged_out` ist ein eigener Grund, kein
      // umbenanntes `membership_required`.
      trackEvent("event_signup_blocked", {
        ...eventProps(event),
        reason: event.memberOnly ? "member_only_logged_out" : "login_required",
      });
    } else if (needsMembership) {
      trackedGate.current = true;
      trackEvent("event_signup_blocked", { ...eventProps(event), reason: "membership_required" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsLogin, needsMembership, memberCheckPending]);

  // Re-check membership when modal opens for member-only events
  useEffect(() => {
    if (open && isLoggedIn && event.memberOnly) {
      refreshMembership();
    }
  }, [open, isLoggedIn, event.memberOnly, refreshMembership]);

  // Fetch existing signups when modal opens
  useEffect(() => {
    if (!open || !isLoggedIn) return;
    fetch(`/api/events/signup?storyblokEventUuid=${event.storyblokEventUuid}`)
      .then((res) => res.ok ? res.json() : { dates: [] })
      .then((data) => {
        const dates = new Set<string>((data.dates || []).map((d: string) => d));
        setSignedUpDates(dates);
        if (!isRecurring) {
          setSingleState(dates.has(event.eventDate) ? "already_signed_up" : "confirm");
        }
      })
      .catch(() => {});
  }, [open, isLoggedIn, event.storyblokEventUuid, isRecurring, event.eventDate]);

  // Refresh signedUpDates (called by DateRow after signup/cancel)
  const refreshDates = () => {
    fetch(`/api/events/signup?storyblokEventUuid=${event.storyblokEventUuid}`)
      .then((res) => res.ok ? res.json() : { dates: [] })
      .then((data) => {
        setSignedUpDates(new Set<string>((data.dates || []).map((d: string) => d)));
      })
      .catch(() => {});
  };

  // Single event handlers
  const handleSingleSignup = async () => {
    setSingleState("loading");
    setError("");
    try {
      const res = await fetch("/api/events/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storyblokEventUuid: event.storyblokEventUuid,
          eventDate: event.eventDate,
          eventTitle: event.eventTitle,
          eventSlug: event.eventSlug,
          eventType: event.eventType,
          eventLink: event.eventLink,
          speakerName: event.speakerName,
          memberOnly: event.memberOnly,
          displayDate: event.eventDisplayDate,
        }),
      });
      if (res.status === 403) {
        // Kein Event hier — der Gate-Effekt oben deckt die Sperre bereits ab.
        refreshMembership();
        setSingleState("confirm");
        return;
      }
      if (res.ok) {
        trackEvent("event_signup", eventProps(event));
        track("Event signup", { event_title: event.eventTitle, event_type: event.eventType });
        refreshSignups();
        setSingleState("success");
      } else if (res.status === 409) {
        setSingleState("already_signed_up");
      } else {
        trackEvent("event_signup_failed", { ...eventProps(event), status: res.status });
        setError("Fehler bei der Anmeldung.");
        setSingleState("confirm");
      }
    } catch {
      setError("Etwas ist schiefgelaufen.");
      setSingleState("confirm");
    }
  };

  const handleSingleCancel = async () => {
    setSingleState("loading");
    setError("");
    try {
      const res = await fetch("/api/events/signup", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storyblokEventUuid: event.storyblokEventUuid,
          eventDate: event.eventDate,
        }),
      });
      if (res.ok) {
        trackEvent("event_signup_cancelled", eventProps(event));
        track("Event signup cancelled", { event_title: event.eventTitle, event_type: event.eventType });
        refreshSignups();
        setSingleState("confirm");
      } else {
        setError("Fehler bei der Abmeldung.");
        setSingleState("already_signed_up");
      }
    } catch {
      setError("Etwas ist schiefgelaufen.");
      setSingleState("already_signed_up");
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSingleState("confirm");
      setError("");
    }, 300);
  };

  // Filter to future dates only
  const futureDates = (event.allDates || []).filter((entry) => {
    const d = new Date(entry.display);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return d >= startOfToday;
  });

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md bg-white rounded-3xl p-6 focus:outline-none">
          <DialogTitle className="sr-only">Event-Anmeldung</DialogTitle>

          {/* Close button */}
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-full p-2 transition-opacity hover:opacity-70 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 23 23" fill="none" aria-hidden="true">
              <path d="M1 21.9705L11.4853 11.4853M11.4853 11.4853L21.9705 1M11.4853 11.4853L1 1M11.4853 11.4853L21.9705 21.9705" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="sr-only">Schließen</span>
          </DialogPrimitive.Close>

          {/* Event header */}
          {(!isRecurring || !canSignup) && singleState !== "success" && singleState !== "already_signed_up" && (
            <div className="flex flex-col items-center gap-2 mt-4 mb-2">
              {/* Schloss hinter dem Badge, wie auf den Karten — und nur bei
                  Member-Only. Es hing zuvor an `needsLogin`, stand also auch
                  auf einem kostenlosen Event, solange niemand angemeldet war:
                  direkt ueber dem Satz "Dieses Event ist kostenlos fuer alle". */}
              {event.eventType && (
                <div className="flex items-center gap-2">
                  <EventTag variant={event.eventType as EventTagVariant} memberOnly={event.memberOnly} />
                  {event.memberOnly && <MemberLockIcon locked={!isMember} />}
                </div>
              )}
              <h3 className="text-headline-md text-black text-center">
                {event.eventTitle}
              </h3>
              {/* Das Datum entfaellt nur, wo direkt darunter die Terminliste
                  folgt — sonst waere ein einzelner Termin irrefuehrend. Ohne
                  Zugang gibt es diese Liste nicht, und der Nutzer soll sehen,
                  wofuer er sich anmeldet oder Mitglied wird. */}
              {(!isRecurring || !canSignup) && (
                <p className="text-copy-sm text-gray-75">{formattedDate}</p>
              )}
            </div>
          )}

          {/* Recurring header */}
          {isRecurring && canSignup && (
            <div className="flex flex-col items-center gap-2 mt-4 mb-4">
              {event.eventType && (
                <EventTag variant={event.eventType as EventTagVariant} memberOnly={event.memberOnly} />
              )}
              <h3 className="text-headline-md text-black text-center">
                {event.eventTitle}
              </h3>
            </div>
          )}

          {/* Nicht eingeloggt — je nach Zugang zwei verschiedene Wege.

              Member-Only fuehrt zur Planauswahl, nicht zum Login: Ein Konto
              allein bringt den Nutzer hier nicht weiter, er braucht eine
              Mitgliedschaft. Der Login geschieht im Kaufweg ohnehin. */}
          {needsLogin && event.memberOnly && (
            <div className="flex flex-col items-center gap-4 text-center">
              <MemberLockIcon locked />
              <p className="text-copy text-gray-75">
                Dieses Event ist exklusiv für Dialetics Member
              </p>
              {/* Erst anmelden, dann zahlen.
                  Ein direkter Link auf /onboarding/membership brachte den
                  Ausgeloggten nicht dorthin: Die Route ist geschuetzt
                  (middleware.ts:14), `auth.protect` haette ihn auf /login
                  geworfen — eine Weiterleitung, die der Knopf nicht
                  ankuendigt. Stattdessen oeffnet das Login-Modal, und
                  `AuthCard` fuehrt nach erfolgreichem Login selbst zur
                  Planauswahl. */}
              <Button
                variant="black"
                size="sm"
                className="w-full"
                onClick={() => {
                  setReturnIntent(event.returnUrl ?? event.eventSlug, "event_access");
                  onOpenChange(false);
                  setAuthModalOpen(true);
                }}
              >
                Jetzt Member werden
              </Button>
              <Button variant="transparent" size="sm" asChild className="w-full">
                {/* Auch dieser Weg setzt den Intent: Wer sich auf /membership
                    ueberzeugen laesst und dort kauft, soll danach beim Event
                    landen — nicht auf dem Dashboard, wo er es neu suchen
                    muesste. */}
                <Link
                  href="/membership"
                  onClick={() => setReturnIntent(event.returnUrl ?? event.eventSlug, "event_access")}
                >
                  Mehr erfahren
                </Link>
              </Button>
            </div>
          )}

          {/* Kostenlos, aber ohne Konto: hier genuegt der Login. */}
          {needsLogin && !event.memberOnly && (
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-copy text-gray-75">
                Dieses Event ist kostenlos für alle. Um dich anzumelden brauchst
                du einen Account bei Dialetics.
              </p>
              {/* Ziel ist das Event, nicht die aktuelle Seite.
                  `window.location.pathname` stimmte nur auf der
                  Event-Detailseite. Aus einer Karte heraus — etwa auf
                  /membership — zeigte der Intent auf die Seite, auf der das
                  Modal geoeffnet wurde, und der Nutzer kam nach dem Login
                  dorthin zurueck statt zum Event. */}
              <Button variant="black" size="sm" asChild className="w-full">
                <Link
                  href={`/login?redirect=${encodeURIComponent(event.returnUrl ?? event.eventSlug)}`}
                  onClick={() => setReturnIntent(event.returnUrl ?? event.eventSlug, "event_signup")}
                >
                  Jetzt anmelden
                </Link>
              </Button>
            </div>
          )}

          {/* Loading: checking membership */}
          {isLoggedIn && event.memberOnly && membershipLoading && (
            <div className="flex justify-center py-4">
              <p className="text-copy text-gray-75">Wird geladen...</p>
            </div>
          )}

          {/* Eingeloggt, aber ohne Mitgliedschaft — dieselbe Aufforderung wie
              fuer Ausgeloggte, nur ohne Login-Schritt.

              Ziel ist `/onboarding/membership`, die Planauswahl. Vorher stand
              hier `/user/membership` — die Verwaltungsseite fuer bestehende
              Mitglieder, also kein Kaufweg. */}
          {needsMembership && (
            <div className="flex flex-col items-center gap-4 text-center">
              <MemberLockIcon locked />
              <p className="text-copy text-gray-75">
                Dieses Event ist exklusiv für Dialetics Member
              </p>
              <Button variant="black" size="sm" asChild className="w-full">
                <Link
                  href="/onboarding/membership"
                  onClick={() => setReturnIntent(event.returnUrl ?? event.eventSlug, "event_access")}
                >
                  Jetzt Member werden
                </Link>
              </Button>
              <Button variant="transparent" size="sm" asChild className="w-full">
                {/* Auch dieser Weg setzt den Intent: Wer sich auf /membership
                    ueberzeugen laesst und dort kauft, soll danach beim Event
                    landen — nicht auf dem Dashboard, wo er es neu suchen
                    muesste. */}
                <Link
                  href="/membership"
                  onClick={() => setReturnIntent(event.returnUrl ?? event.eventSlug, "event_access")}
                >
                  Mehr erfahren
                </Link>
              </Button>
            </div>
          )}

          {/* ===== RECURRING: inline date list with signup/cancel per row ===== */}
          {isRecurring && canSignup && (
            <div className="flex flex-col gap-2 max-h-[350px] overflow-y-auto">
              {futureDates.map((entry) => (
                <DateRow
                  key={entry.original}
                  entry={entry}
                  isSignedUp={signedUpDates.has(entry.original)}
                  event={event}
                  onUpdate={refreshDates}
                  onMembershipRequired={refreshMembership}
                />
              ))}
            </div>
          )}

          {/* ===== SINGLE EVENT: confirm / success / already_signed_up ===== */}
          {!isRecurring && canSignup && singleState === "confirm" && (
            <div className="flex flex-col items-center gap-4">
              {error && (
                <p className="text-copy-sm text-center [color:hsl(var(--red-100))]">{error}</p>
              )}
              <Button variant="black" size="sm" className="w-full" onClick={handleSingleSignup}>
                Ich melde mich an
              </Button>
            </div>
          )}

          {!isRecurring && canSignup && singleState === "loading" && (
            <div className="flex justify-center py-4">
              <p className="text-copy text-gray-75">Wird geladen...</p>
            </div>
          )}

          {!isRecurring && singleState === "already_signed_up" && (
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircleFilled className="size-8 text-black" />
              <div className="flex flex-col items-center gap-1">
                <p className="text-headline-md text-black">Du bist angemeldet</p>
                <p className="text-copy text-black">
                  {event.eventTitle} • {formattedDate}
                </p>
              </div>
              {error && (
                <p className="text-copy-sm [color:hsl(var(--red-100))]">{error}</p>
              )}
              <Button variant="black" size="sm" asChild className="w-full">
                <Link href="/dashboard">
                  Meine Events
                  <ArrowRight />
                </Link>
              </Button>
              <button
                type="button"
                onClick={handleSingleCancel}
                className="text-copy-sm text-gray-75 hover:underline hover:text-black transition-colors"
              >
                Abmelden
              </button>
            </div>
          )}

          {!isRecurring && singleState === "success" && (
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircleFilled className="size-8 text-black" />
              <div className="flex flex-col items-center gap-1">
                <p className="text-headline-md text-black">Du bist angemeldet</p>
                <p className="text-copy text-black">
                  {event.eventTitle} • {formattedDate}
                </p>
              </div>
              <Button variant="black" size="sm" asChild className="w-full">
                <Link href="/dashboard">
                  Meine Events
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export { EventSignupModal };
export type { EventSignupData };
